import { useState, useEffect } from 'react';
import { Theme, applyTheme, persistTheme, readStoredTheme, SHARED_THEME_KEY, LEGACY_THEME_KEY, syncThemeUrl } from '@/utils/siteSync';

export function ThemeToggle() {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());

  useEffect(() => {
    const initial = readStoredTheme();
    setThemeState(initial);
    applyTheme(initial);
    syncThemeUrl(initial);

    // Sync with main site / other tabs
    const onStorage = (e: StorageEvent) => {
      if (e.key === SHARED_THEME_KEY || e.key === LEGACY_THEME_KEY) {
        const next = readStoredTheme({ includeUrl: false });
        setThemeState(next);
        applyTheme(next);
        syncThemeUrl(next);
      }
    };
    // Re-read when tab becomes visible (covers same-domain returns)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const next = readStoredTheme({ includeUrl: false });
        setThemeState(next);
        applyTheme(next);
        syncThemeUrl(next);
      }
    };
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setThemeState(next);
    persistTheme(next);
    applyTheme(next);
    syncThemeUrl(next);
  };

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label="Vaihda teemaa"
      aria-pressed={theme === 'dark'}
    >
      <span className="theme-toggle__icon" aria-hidden="true">
        <span className="theme-toggle__glyph">
          <svg className="theme-toggle__moon" viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
            <path
              fill="currentColor"
              d="M16.2 27.4a11.2 11.2 0 0 0 10.9-13.6.8.8 0 0 0-1.2-.5 8.8 8.8 0 0 1-4.4 1.2 8.8 8.8 0 0 1-8.8-8.8 8.6 8.6 0 0 1 1-4.1.8.8 0 0 0-.8-1.1 11.2 11.2 0 1 0 3.3 26.9Z"
            />
          </svg>
          <svg className="theme-toggle__sun" viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
            <circle cx="16" cy="16" r="6" fill="currentColor" />
            <g stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M16 4.5V2" />
              <path d="m22.3 9.7 1.8-1.8" />
              <path d="M27.5 16h2.5" />
              <path d="m22.3 22.3 1.8 1.8" />
              <path d="M16 27.5V30" />
              <path d="m9.7 22.3-1.8 1.8" />
              <path d="M4.5 16H2" />
              <path d="m9.7 9.7-1.8-1.8" />
            </g>
          </svg>
        </span>
      </span>
    </button>
  );
}
